/**
 * KioskCloseButton — solid floating "X" in the top-right of the kiosk
 * fullscreen view. One click → hides the window to tray. The PC/SC
 * reader bridge stays alive, so the next chip tap pops it back up.
 *
 * Confirmation step was removed: it's just a hide-to-tray (reversible,
 * non-destructive), and teachers reported the "Kiosk schließen? Ja /
 * Nein" prompt as friction.
 *
 * No internal state needed — the IPC call is sub-millisecond and the
 * button doesn't need to track in-flight state. Previously there was a
 * `closing` flag to prevent double-clicks during the await, but it was
 * never reset (the window hides before the resolve handler can run),
 * so the second time the kiosk came back from tray the button was
 * permanently disabled. Just fire and forget.
 *
 * If the page is loaded outside the Electron shell (no window.kiosk),
 * the button is hidden entirely.
 */
import { X } from "lucide-react";

interface KioskCloseButtonProps {
  /** Position the button. Default "top-right" — matches the OS title-bar X. */
  position?: "bottom-right" | "top-right";
  /** Caller-controlled cleanup that runs BEFORE the window hides to tray.
   *  Use this to reset session state, clear RTK Query cache, etc., so the
   *  next teacher who taps a chip doesn't briefly see the previous one's
   *  data in any leftover in-memory state. */
  onBeforeClose?: () => void;
}

export function KioskCloseButton({
  position = "top-right",
  onBeforeClose,
}: KioskCloseButtonProps) {
  if (typeof window === "undefined" || !window.kiosk?.hideToTray) {
    return null;
  }

  const posClasses =
    position === "top-right"
      ? "top-4 right-4"
      : "bottom-4 right-4";

  const handleClick = () => {
    // Run caller-provided cleanup first (state reset, RTK cache clear).
    try { onBeforeClose?.(); } catch { /* never let cleanup block close */ }
    // Fire-and-forget hide. Explicit .catch() so an IPC rejection
    // (channel gone, renderer tearing down) doesn't surface as an
    // unhandledrejection in Sentry.
    window.kiosk?.hideToTray?.().catch(() => {
      // best-effort; nothing the user can act on here
    });
  };

  return (
    <button
      onClick={handleClick}
      className={`fixed ${posClasses} z-50 w-12 h-12 rounded-full bg-slate-800 hover:bg-slate-900 text-white shadow-lg flex items-center justify-center transition-colors`}
      title="Kiosk ausblenden"
      aria-label="Kiosk ausblenden"
    >
      <X className="h-6 w-6" strokeWidth={2.5} />
    </button>
  );
}
